import React, { useMemo } from "react";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";

import colors, { blue, slate, selection } from "../../theme/colors";

/**
 * Applies the Lendmarq color system (theme/lendmarq-color-system.md) to
 * everything inside it — the palette plus the component patterns the doc
 * specifies.
 *
 * WHY THIS EXISTS. The vendored tpo tree reaches for `primary.main` wherever it
 * wants the brand color — the step eyebrow, selected toggle pills, the send
 * screen's kind selector, the assign-loan dialog's selected row. In tpo that IS
 * the brand. In this app it is **navy `#1B3A4B`**: `generateMuiThemeDefaults`
 * defaults the palette to navy and `src/index.js` passes no `primaryColor`
 * override. So every one of those accents rendered dark instead of blue, and
 * recoloring them one at a time just moves the problem to the next call site.
 *
 * A nested provider fixes them all at the root, and leaves the rest of the app
 * on navy — this is scoped to the vendored surfaces, not global.
 *
 * It extends the surrounding theme rather than building a fresh one, so every
 * other override (typography, component variants, spacing) is inherited. The
 * component overrides below are additive for the same reason: they state the
 * doc's rules once, and any call site that already passes its own `sx` still
 * wins, so nothing that was deliberately styled changes underneath it.
 *
 * NOT here: the blue header (doc §5). The app header lives outside this
 * provider, in AuthenticatedDashboard/TopHeader.js, and already paints itself
 * with the header blue + white-on-blue nav the doc describes.
 */
const BrandThemeProvider = ({ children }) => {
  const parent = useTheme();

  const theme = useMemo(
    () =>
      createTheme(parent, {
        palette: {
          // Brand = action for Lendmarq: #1F5AF2 clears AA under white text, so
          // there is no separate "never put white text on this" accent step.
          primary: {
            main: colors.action,
            light: blue[400],
            dark: colors.actionHover,
            contrastText: colors.white,
          },
          // Second accent hue. Neutral is still the rule for secondary
          // BUTTONS — this is for charts and second data dimensions.
          secondary: { main: colors.violet, contrastText: colors.white },
          info: { main: colors.action },
        },

        components: {
          MuiButton: {
            styleOverrides: {
              containedPrimary: {
                "&:hover": { backgroundColor: blue[700] },
                "&:active": { backgroundColor: blue[800] },
                "&.Mui-focusVisible": { boxShadow: selection.focusRing },
              },
              // Secondary buttons are neutral — white, slate border, slate
              // text. One colored button per view.
              outlined: {
                borderColor: slate[300],
                color: slate[700],
                "&:hover": {
                  borderColor: slate[400],
                  backgroundColor: slate[50],
                },
              },
            },
          },

          // Tabs are navigation, so they keep the underline pattern rather than
          // the tinted chip below — the two looks staying distinct is what tells
          // a user which is which.
          MuiTabs: {
            styleOverrides: {
              indicator: { backgroundColor: blue[600], height: 2 },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 500,
                color: slate[500],
                "&.Mui-selected": { color: blue[600], fontWeight: 600 },
              },
            },
          },

          // The signature selected state, applied to ToggleButton so a
          // <ToggleButtonGroup exclusive> renders Lendmarq choice chips
          // natively. Form cards additionally spread TOGGLE_PILL_SX, which
          // pills the corners and wins on specificity — same pattern, tuned
          // for a form row.
          MuiToggleButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 500,
                borderColor: selection.restBorder,
                color: selection.restText,
                "&:hover": {
                  backgroundColor: selection.hoverBg,
                  borderColor: selection.hoverBorder,
                },
                "&.Mui-selected": {
                  backgroundColor: selection.bg,
                  borderColor: selection.border,
                  color: selection.text,
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: selection.bg,
                    borderColor: selection.selectedHoverBorder,
                    color: selection.selectedHoverText,
                  },
                },
                "&.Mui-focusVisible": { boxShadow: selection.focusRing },
              },
            },
          },

          // Progress fills use the action blue — this system has no separate
          // accent hue to reserve for them.
          MuiLinearProgress: {
            styleOverrides: {
              root: { backgroundColor: slate[200], borderRadius: 99 },
              bar: { backgroundColor: blue[600], borderRadius: 99 },
            },
          },

          MuiLink: {
            defaultProps: { underline: "hover" },
            styleOverrides: { root: { color: blue[600], fontWeight: 500 } },
          },

          // Focused field: brand border plus the blue-100 halo. The parent
          // theme already focuses on #1F5AF2; this restates the halo in ramp
          // terms so both come from the same source.
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: blue[600],
                  borderWidth: 1,
                },
                "&.Mui-focused": { boxShadow: `0 0 0 3px ${blue[100]}` },
              },
            },
          },
        },
      }),
    [parent],
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default BrandThemeProvider;
